import 'flatpickr';
import { DateTime } from 'luxon';

document.addEventListener('DOMContentLoaded', () => {
    init(document);
    const observer = new MutationObserver((mutations) => {
        mutations.filter((mutation) => mutation.addedNodes.length)
            .filter((mutation) => mutation.target.querySelectorAll)
            .forEach((mutation) => init(mutation.target));
    });
    observer.observe(document.body, { childList: true, subtree: true });
});

function init(target) {
    ['date', 'month'].forEach((type) => {
        Array.from(target.querySelectorAll(`.form-control-${type}picker`)).forEach((node) => {
            initFlatpickr(node, type);
        });
    });
}

function initFlatpickr(node, type) {
    if (
        node._flatpickr
        || node._mobileConfigured
        || $(node).closest('[data-edit-row-template]').length
    ) {
        // Flatpickr has been initialised for this node
        // or element is a template (whose clone should be initialised when created).
        return;
    }

    const config = {};

    // From flatpickr@v2.4.2 - https://github.com/chmln/flatpickr/blob/v2.4.2/dist/flatpickr.js:43
    const mobileRe = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

    if (mobileRe.test(navigator.userAgent)) { // is "mobile" according to flatpickr
        if (node.classList.contains(`form-control-${type}picker-disablemobile`)) {
            // use Flatpickr calendar for this node on mobile devices
            config.disableMobile = true;
        } else {
            // bypass Flatpickr's buggy native mobile input support
            node.setAttribute('type', type);
            handleMobileReadOnlyNode(node);
            node._mobileConfigured = true;
            return;
        }
    }

    const isModifiable = !node.hasAttribute('disabled') && !node.hasAttribute('readonly');
    config.allowInput = isModifiable;
    config.altFormat = type === 'date' ? 'j M Y' : 'F Y';
    config.altInput = true;
    config.clickOpens = isModifiable;
    config.static = node.classList.contains(`form-control-${type}picker-static`);

    /**
     * Flatpickr calls onChange when a value is selected from the calendar and when a value
     * is cleared manually. It also fires a change event. Called after onValueUpdate.
     */
    config.onChange = (selectedDates, dateStr, instance) => {
        instance.willDispatchEvent = true;
    };

    /**
     * Flatpickr calls onClose when the calendar is closed.
     */
    config.onClose = (selectedDates, dateStr, instance) => {
        let expectedAltInputValue = '';

        if (instance.input.value !== '') {
            const date = instance.parseDate(instance.input.value);
            expectedAltInputValue = instance.formatDate(date, instance.config.altFormat);
        }

        let expectedInputValue = '';

        if (instance.altInput.value !== '') {
            const date = instance.parseDate(instance.altInput.value);
            expectedInputValue = DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
        }

        if (instance.altInput.value !== expectedAltInputValue
            || instance.input.value !== expectedInputValue) {
            instance.setDate(instance.altInput.value, true);
        }
    };

    /**
     * Flatpickr calls onOpen when the calendar is opened, adjust the position of calendar
     * and arrow based on the distance from the bottom of page.
     */
    config.onOpen = (selectedDates, dateStr, instance) => {
        const calendarHeight = instance.calendarContainer.offsetHeight;
        const input = instance.altInput || instance.input;
        const inputBounds = input.getBoundingClientRect();
        const distanceFromBottom = (window.innerHeight - inputBounds.bottom)
            + input.offsetHeight;

        if (distanceFromBottom < calendarHeight) {
            instance.calendarContainer.style.top = `-${calendarHeight + 3}px`;
            instance.calendarContainer.classList.remove('arrowTop');
            instance.calendarContainer.classList.add('arrowBottom');
        } else {
            instance.calendarContainer.style.top = `${input.offsetHeight + 3}px`;
            instance.calendarContainer.classList.remove('arrowBottom');
            instance.calendarContainer.classList.add('arrowTop');
        }
    };

    /**
     * Flatpickr calls onValueUpdate when an initial value is set, when a value is selected
     * from the calendar and when a value is entered manually. Called before onChange.
     */
    config.onValueUpdate = (selectedDates, dateStr, instance) => {
        instance.willDispatchEvent = false;
        window.setTimeout(() => {
            if (!instance.willDispatchEvent) {
                // Dispatch change event when Flatpickr doesn't call onChange
                instance.input.dispatchEvent(new Event('change'));
            }
        }, 100);
    };

    /**
     * Flatpickr calls parseDate to parse the given value into a Date object.
     */
    config.parseDate = (date) => {
        const formats = type === 'date'
            ? ['ddMMyy', 'ddMMyyyy', 'dd/MM/yyyy', 'dd/MM/yy', 'dd/M/yyyy', 'dd/M/yy', 'd LLL yyyy', 'cccc, d LLLL yyyy', 'dd-MM-yyyy', 'dd-M-yyyy', 'dd-MM-yy', 'dd-M-yy', 'dd MM yy', 'yyyy-MM-dd']
            : ['MM/yyyy', 'MM/yy', 'MMMM yyyy', 'MM-yyyy', 'MM/yy', 'MM-yy', 'MM yy', 'yyyy-MM'];

        // If it's a valid date, Luxon will have null invalidReason
        const validFormat = formats.find((format) => DateTime.fromFormat(date, format).isValid);
        const parsedDate = validFormat ? DateTime.fromFormat(date, validFormat) : DateTime.now();
        return parsedDate.toJSDate();
    };

    if (node.hasAttribute('data-alt-format')) {
        // Flatpickr uses the dataset property which isn't supported in IE10
        config.altFormat = node.getAttribute('data-alt-format');
    }

    if (node.hasAttribute('data-min-date')) {
        config.minDate = node.getAttribute('data-min-date');
    }

    if (!config.static) delete config.onOpen;

    node.flatpickr(config);
    node._flatpickr.altInput.addEventListener('blur', () => {
        if (!$(node._flatpickr.calendarContainer).is(':hover')) node._flatpickr.close();
    });
    const className = 'flatpickr-input form-control form-control-icon-calendar';
    node._flatpickr.altInput.className = className;
    node._flatpickr.altInput.setAttribute(
        'aria-label',
        document.querySelector(`label[for=${node.id}]`).textContent,
    );
    node._flatpickr.altInput.removeAttribute('required');
    ['disabled', 'readonly'].forEach((attribute) => (
        node.hasAttribute(attribute)
            ? node._flatpickr.altInput.setAttribute(attribute, '')
            : node._flatpickr.altInput.removeAttribute(attribute)
    ));

    node._flatpickrObserver = new MutationObserver(() => {
        node._flatpickrObserver.disconnect();
        const { readOnly } = node;
        const { value } = node;
        node._flatpickr.destroy();
        delete node._flatpickr;
        node.readOnly = readOnly;
        node.value = value;
        initFlatpickr(node, type);
    });
    node._flatpickrObserver.observe(node, {
        attributeFilter: ['disabled', 'readonly'],
        attributes: true,
    });
}

function handleMobileReadOnlyNode(node) {
    // initialise the event when read only
    handleMobileReadOnlyEvents(node);

    // add observer to monitor the read only attribute
    const datePickerObserver = new MutationObserver((mutationsList) => {
        mutationsList.forEach((mutatedNode) => {
            handleMobileReadOnlyEvents(mutatedNode.target);
        });
    });
    datePickerObserver.observe(node, {
        attributes: true,
        attributeFilter: ['readonly'],
    });
}

function handleMobileReadOnlyEvents(node) {
    if (node.hasAttribute('readonly')) {
        node.addEventListener('touchstart', blockEvent);
    } else {
        node.removeEventListener('touchstart', blockEvent);
    }
}

function blockEvent(evt) {
    evt.preventDefault();
}
